/* eslint-disable import/prefer-default-export */
import { useCallback, useContext } from 'react';
import { getAnalytics, logEvent } from 'firebase/analytics';
import { carryForwardModeOfColumns } from 'components/utils/carryForwardOnContinue';
import { fillingStatusFromValue } from '../../utils/fillingType';
import { getOperationContainerPath } from '../utils/containerSearch';
import {
  getOldFormValues,
  determineNewInventoryStatus,
  getNewVolumeStatusDone,
  getNewABVStatusDone,
  updateContainerWithMappedValues,
  calculateProgressPercentageABV,
  calculateProgressPercentageVolume,
} from '../utils/operationUtils';
import { CARRIED_FROM_KEY, pickCarryForwardValues } from '../utils/carryForward';
import { InventoryQueueRegistryContext } from './useInventoryQueueRegistry';

/**
 * Custom hook to handle operation recording and offline sync
 */
export function useOperation(
  globalState,
  currentInventoryData,
  setCurrentInventoryData,
  currentContainerData,
  getContainerId,
  currentContainer,
  containerType,
  t,
  globalActions,
  navigate,
) {
  const analytics = getAnalytics();
  const queueRegistry = useContext(InventoryQueueRegistryContext);

  // Navigation utilities
  const nextOperation = useCallback((updatedData = ({
    updatedInventoryData: currentInventoryData,
  }), updatedPreviousItemFormValues = undefined) => {
    const activeContainer = currentContainer();

    // Find the index of the current item in the inventory
    const currentIndex = updatedData.updatedInventoryData.containersArray.findIndex(
      (container) => container.id === activeContainer?.id,
    );

    if (currentIndex >= 0 && currentIndex < updatedData.updatedInventoryData.containersArray.length - 1) {
      const nextItemData = updatedData.updatedInventoryData.containersArray[currentIndex + 1];

      navigate(getOperationContainerPath(
        nextItemData,
        currentInventoryData.mappingConfig.columns.ivtContainerId.mapping.csvName,
      ), {
        state: {
          containerId: nextItemData.id,
          inventoryData: updatedData.updatedInventoryData,
          previousItemFormValues: updatedPreviousItemFormValues,
        },
        replace: true,
      });
    } else {
      navigate(-1);
    }
  }, [
    navigate,
    currentInventoryData,
    getContainerId,
    currentContainer,
  ]);

  // carriedValues: the values carried into this container (its previousItemFormValues)
  const recordOperation = useCallback(async (values, processToNext, carriedValues = undefined) => {
    const activeContainer = currentContainer();

    logEvent(analytics, 'recordOperation', {
      appName: 'Inventory',
      organization: globalState.activeOrganization,
    });
    console.log('[OP] recordOperation', values, processToNext);

    const oldFormValues = getOldFormValues(
      containerType,
      t,
      currentContainerData,
      currentInventoryData.mappingConfig,
      globalState,
    );

    // Write record and link to an inventoryUpdate.
    // Group pending records until Firestore acknowledges them, regardless of network hints.
    try {
      const payload = {
        containerId: getContainerId(currentContainer()),
        formValues: values,
        oldFormValues,
        fillingStatus: fillingStatusFromValue(
          currentContainerData[currentInventoryData.mappingConfig.columns.filling.mapping.csvName],
        ),
      };

      if (queueRegistry) {
        // Keep the form until its LOCAL commit succeeds, including while offline.
        await queueRegistry.enqueueRecord(currentInventoryData.id, payload);
      } else {
        throw new Error('InventoryQueueRegistry not available');
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to stage operation writes', error);
      try {
        globalActions.setSnackbarMessage({
          message: `${t('unexpected_error')} : ${error.message}`,
          severity: 'warning',
        });
      } catch (_) { /* no-op */ }
      throw error;
    }

    // Calculate filling status for status updates
    const fillingStatus = fillingStatusFromValue(
      currentContainerData[currentInventoryData.mappingConfig.columns.filling.mapping.csvName],
    );

    // Update the status of the container in containersArray
    const updatedContainersArray = currentInventoryData.containersArray.map((container) => {
      if (container.id === activeContainer?.id) {
        const updatedContainer = updateContainerWithMappedValues(
          container,
          values,
          currentInventoryData.mappingConfig,
          containerType,
        );

        return {
          ...updatedContainer,
          abvStatus: getNewABVStatusDone(fillingStatus, containerType, values) ? 'done' : 'toDo',
          volumeStatus: getNewVolumeStatusDone(fillingStatus, containerType, values) ? 'done' : 'toDo',
        };
      }
      return container;
    });

    const progressABVPercentage = calculateProgressPercentageABV(updatedContainersArray);
    const progressVolumePercentage = calculateProgressPercentageVolume(updatedContainersArray);

    const newStatus = determineNewInventoryStatus(
      currentInventoryData.status,
      progressABVPercentage,
      progressVolumePercentage,
    );

    const updates = {
      containersArray: updatedContainersArray,
      progressABVPercentage,
      progressVolumePercentage,
      status: newStatus,
    };

    globalActions.updateLocalInventory(currentInventoryData.id, updates);

    const completeInventoryUpdate = { ...currentInventoryData, ...updates };
    setCurrentInventoryData(completeInventoryUpdate);

    if (processToNext) {
      const updatedData = {
        updatedInventoryData: completeInventoryUpdate,
      };
      const pickedValues = pickCarryForwardValues({
        values,
        storedValues: oldFormValues,
        carriedValues,
        carryForwardModeOf: carryForwardModeOfColumns(currentInventoryData.mappingConfig.columns),
        customFields: currentInventoryData.mappingConfig.customFields,
      });
      // The next container shows where carried values come from
      const carriedFrom = currentContainerData?.[
        currentInventoryData.mappingConfig.columns.ivtContainerId?.mapping?.csvName
      ];
      const carryForwardValues = Object.keys(pickedValues).length > 0 && carriedFrom
        ? { ...pickedValues, [CARRIED_FROM_KEY]: String(carriedFrom) }
        : pickedValues;
      return { shouldNavigateToNext: true, updatedData, carryForwardValues };
    }
    navigate(-1);
    return { shouldNavigateToNext: false };
  }, [analytics, globalState, currentInventoryData, containerType,
    t, currentContainerData, globalActions, setCurrentInventoryData, navigate,
    getContainerId, currentContainer, queueRegistry]);

  return { recordOperation, nextOperation };
}
